'use client'

import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useLocale, useTranslations } from 'next-intl'
import { z } from 'zod'
import Flag from 'react-flagkit'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Editor } from '@/components/ui/editor'
import { Combobox, ComboboxOption } from '@/components/ui/combobox'
import { CategoryCombobox } from '@/components/ui/category-combobox'
import {
  CategoryPropertyInput,
  CategoryTranslationParams,
  CategoryTranslationsParams,
  CategoryType
} from '@/types/category'
import {
  FolderTreeIcon,
  GripVerticalIcon,
  ImageIcon,
  LanguagesIcon,
  PlusCircleIcon,
  SaveIcon,
  TagIcon,
  Trash2Icon,
  XIcon
} from 'lucide-react'
import { useMetaStore } from '@/store'
import { useScrollToError, useUnsavedChangesGuard } from '@/hooks'
import { setValidationErrors } from '@/lib/utils'
import { Separator } from '@/components/ui/separator'
import { FileSelector, UnsavedChangesGuardDialog } from '@/components/partials'
import { FileType } from '@/types/file'
import { Switch } from '@/components/ui/switch'
import { FormModeEnum, SortEnum } from '@/enums'
import { useProperties } from '@/hooks/use-properties'
import { PropertyType } from '@/types/property'

type PropsType = {
  category?: CategoryType
  onSubmit: (data: any) => void
  onSubmitAndNew?: (data: any) => void
  onCancel: () => void
  isLoading?: boolean
  mode: FormModeEnum
}

export type CategoryFormRef = {
  handleValidationErrors: (error: any) => void
  reset: () => void
}

type FormData = {
  parentId?: string
  pictureId?: string
  active?: boolean
  translations: CategoryTranslationsParams
  properties?: CategoryPropertyInput[]
}

// Sortable property item component
type SortablePropertyItemProps = {
  property: PropertyType
  order: number
  onRemove: () => void
  locale: string
  locales: any[]
}

const SortablePropertyItem = ({ property, order, onRemove, locale, locales }: SortablePropertyItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: property.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  }

  // Find the locale object that matches the current locale
  const currentLocaleObj = locales.find((l: any) => l.fullCode === locale || l.code === locale)

  // Get property name from translation
  let propertyName: string
  if (currentLocaleObj && property.translations[currentLocaleObj.fullCode]) {
    propertyName = property.translations[currentLocaleObj.fullCode].name || property.key
  } else {
    // Fallback to the first available translation
    const firstTranslation = Object.values(property.translations)[0]
    propertyName = firstTranslation?.name || property.key
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-background flex items-center justify-between gap-3 rounded-md border p-3">
      <div className="flex items-center gap-3">
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
          <GripVerticalIcon className="text-muted-foreground h-5 w-5" />
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="font-mono">
            {order}
          </Badge>
          <div>
            <div className="font-medium">{propertyName}</div>
            <div className="text-muted-foreground text-xs">{property.key}</div>
          </div>
        </div>
      </div>
      <Button type="button" variant="ghost" size="icon" onClick={onRemove}>
        <Trash2Icon className="h-4 w-4" />
      </Button>
    </div>
  )
}

const CategoryForm = forwardRef<CategoryFormRef, PropsType>(
  ({ category, onSubmit, onSubmitAndNew, onCancel, isLoading = false, mode }, ref) => {
    const t = useTranslations()
    const locale = useLocale()
    const { scrollToFirstError } = useScrollToError()

    const { metaState } = useMetaStore()
    const { data: propertiesData, isLoading: isLoadingProperties } = useProperties({
      size: 999999,
      sortBy: 'order',
      sort: SortEnum.ASC
    })
    const locales = useMemo(() => metaState.response?.locales || [], [metaState.response?.locales])

    // State for picture
    const [picture, setPicture] = useState<FileType | null>(category?.picture || null)

    // State for selected properties with their full data
    const [selectedProperties, setSelectedProperties] = useState<PropertyType[]>([])

    // Set p sensors for drag and drop
    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 8
        }
      }),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates
      })
    )

    // Initialize selected properties from category data
    useEffect(() => {
      if (category?.properties && propertiesData) {
        const sortedProps = [...category.properties].sort((a, b) => a.order - b.order).map(cp => cp.property)
        setSelectedProperties(sortedProps)
      }
    }, [category, propertiesData])

    const getCountryCodeFromLocale = (localeCode: string): string => {
      const parts = localeCode.split('-')
      return parts.length > 1 ? parts[1] : parts[0].toUpperCase()
    }

    const hasLocaleError = (localeCode: string, field: keyof CategoryTranslationParams): boolean => {
      return !!formErrors.translations?.[localeCode]?.[field]
    }

    const schema = useMemo(() => {
      const translationsSchema: Record<
        string,
        z.ZodObject<{ name: z.ZodString; description: z.ZodOptional<z.ZodString> }>
      > = {}

      locales.forEach(locale => {
        translationsSchema[locale.fullCode] = z.object({
          name: z.string().min(1, t('not_blank')),
          description: z.string().optional()
        })
      })

      return z.object({
        parentId: z.string().optional(),
        pictureId: z.string().optional(),
        active: z.boolean().optional(),
        translations: z.object(translationsSchema),
        properties: z
          .array(
            z.object({
              id: z.string(),
              order: z.number()
            })
          )
          .optional()
      })
    }, [locales, t])

    const getDefaultValues = useCallback((): FormData => {
      const defaultTranslations: CategoryTranslationsParams = {}

      locales.forEach(locale => {
        const translation = category?.translations?.[locale.fullCode]
        defaultTranslations[locale.fullCode] = {
          name: translation?.name ?? '',
          description: translation?.description ?? ''
        }
      })

      return {
        parentId: category?.parents?.length ? category.parents[category.parents.length - 1].id : undefined,
        pictureId: category?.picture?.id ?? undefined,
        active: mode === FormModeEnum.CREATE ? true : (category?.active ?? false),
        translations: defaultTranslations,
        properties: []
      }
    }, [locales, category, mode])

    type SchemaType = z.infer<typeof schema>

    const form = useForm<SchemaType>({
      resolver: zodResolver(schema),
      defaultValues: getDefaultValues() as SchemaType,
      mode: 'onChange'
    })

    const { isNavigationBlocked, confirmNavigation, cancelNavigation } = useUnsavedChangesGuard(form.formState.isDirty)

    // Update form values when category data is available (for UPDATE mode)
    useEffect(() => {
      if (category && mode === FormModeEnum.UPDATE) {
        // Update parent category selection
        if (category.parents && category.parents.length > 0) {
          const parentId = category.parents[category.parents.length - 1].id
          form.setValue('parentId', parentId)

          // Force a re-render of the form to ensure the combobox displays the selected value
          setTimeout(async () => {
            await form.trigger('parentId')
          }, 0)
        }

        // Update translations
        if (category.translations) {
          locales.forEach(locale => {
            if (category.translations[locale.fullCode]) {
              form.setValue(`translations.${locale.fullCode}.name`, category.translations[locale.fullCode].name || '')
              form.setValue(
                `translations.${locale.fullCode}.description`,
                category.translations[locale.fullCode].description || ''
              )
            }
          })
        }
      }
    }, [category, form, locales, mode])

    const formErrors = form.formState.errors

    const handlePictureChange = (files: FileType | FileType[] | null) => {
      if (files === null) {
        setPicture(null)
        form.setValue('pictureId', '', { shouldValidate: true })
      } else if ('length' in files) {
        setPicture(files[0])
        form.setValue('pictureId', files[0]?.id || '', { shouldValidate: true })
      } else {
        setPicture(files)
        form.setValue('pictureId', files?.id || '', { shouldValidate: true })
      }
    }

    // Handle property selection from combobox
    const handlePropertySelect = (propertyId: string | string[]) => {
      if (!propertiesData) {
        return
      }

      // Handle only string values (we're not using multiple selection for properties)
      if (typeof propertyId !== 'string' || !propertyId) {
        return
      }

      const property = propertiesData.items.find(p => p.id === propertyId)
      if (property && !selectedProperties.find(p => p.id === propertyId)) {
        setSelectedProperties([...selectedProperties, property])
      }
    }

    // Handle property removal
    const handlePropertyRemove = (propertyId: string) => {
      setSelectedProperties(selectedProperties.filter(p => p.id !== propertyId))
    }

    // Handle drag end for properties
    const handlePropertyDragEnd = (event: DragEndEvent) => {
      const { active, over } = event

      if (over && active.id !== over.id) {
        setSelectedProperties(items => {
          const oldIndex = items.findIndex(item => item.id === active.id)
          const newIndex = items.findIndex(item => item.id === over.id)
          return arrayMove(items, oldIndex, newIndex)
        })
      }
    }

    // Get available properties (not already selected)
    const availableProperties = useMemo(() => {
      if (!propertiesData) {
        return []
      }
      return propertiesData.items.filter(p => !selectedProperties.find(sp => sp.id === p.id))
    }, [propertiesData, selectedProperties])

    // Convert properties to combobox options
    const propertyOptions: ComboboxOption[] = useMemo(() => {
      return availableProperties.map(property => {
        const currentLocaleObj = locales.find((l: any) => l.fullCode === locale || l.code === locale)
        let name

        if (currentLocaleObj && property.translations[currentLocaleObj.fullCode]) {
          name = property.translations[currentLocaleObj.fullCode].name || property.key
        } else {
          const firstTranslation = Object.values(property.translations)[0]
          name = firstTranslation?.name || property.key
        }

        return {
          label: name,
          value: property.id,
          description: property.key
        }
      })
    }, [availableProperties, locale, locales])

    useImperativeHandle(
      ref,
      () => ({
        handleValidationErrors: (error: any) => {
          if (error.response?.status === 422) {
            const errorItems = error.response.data.items || {}

            // Extract field validation errors
            const fieldErrors: Record<string, string> = {}
            Object.entries(errorItems).forEach(([key, message]) => {
              // Check if the error is related to a field
              if (key.startsWith('fields[')) {
                // Convert array notation to dot notation for the fieldErrors object
                const fieldPath = key.replace(/\[([^\]]+)]/g, '.$1')
                fieldErrors[fieldPath] = message as string
              }
            })

            // Set form validation errors
            setValidationErrors(error, form.setError)
          }
          scrollToFirstError()
        },
        reset: () => form.reset()
      }),
      [form, scrollToFirstError]
    )

    const handleSubmit = (data: SchemaType) => {
      const submissionData: {
        parentId?: string
        pictureId?: string
        active?: boolean
        translations: CategoryTranslationsParams
        properties?: CategoryPropertyInput[]
      } = {
        translations: {} as CategoryTranslationsParams
      }

      // Handle parentId based on mode
      if (mode === FormModeEnum.CREATE) {
        // In CREATE mode: only add parentId if a parent is selected
        if (data.parentId) {
          submissionData.parentId = data.parentId
        }
      } else {
        // In UPDATE mode: always send parentId, empty string to clear
        submissionData.parentId = data.parentId || ''
      }

      // Handle pictureId based on mode
      if (mode === FormModeEnum.CREATE) {
        // In CREATE mode: only add pictureId if a picture is selected
        if (picture?.id) {
          submissionData.pictureId = picture.id
        }
      } else {
        // In UPDATE mode: always send pictureId, empty string to clear
        submissionData.pictureId = picture?.id || ''
      }

      // Only add active if it's defined (to allow setting false)
      if (data.active !== undefined) {
        submissionData.active = data.active
      }

      // Add translations, ensuring all locales are included
      locales.forEach(locale => {
        submissionData.translations[locale.fullCode] = {
          name: data.translations[locale.fullCode]?.name || '',
          description: data.translations[locale.fullCode]?.description || ''
        }
      })

      // Add properties with order
      if (selectedProperties.length > 0) {
        submissionData.properties = selectedProperties.map((property, index) => ({
          id: property.id,
          order: index + 1
        }))
      }

      onSubmit(submissionData)
    }

    const handleSubmitAndNew = (data: SchemaType) => {
      const submissionData: {
        parentId?: string
        pictureId?: string
        active?: boolean
        translations: CategoryTranslationsParams
        properties?: CategoryPropertyInput[]
      } = {
        translations: {} as CategoryTranslationsParams
      }

      // Handle parentId based on mode
      if (mode === FormModeEnum.CREATE) {
        // In CREATE mode: only add parentId if a parent is selected
        if (data.parentId) {
          submissionData.parentId = data.parentId
        }
      } else {
        // In UPDATE mode: always send parentId, empty string to clear
        submissionData.parentId = data.parentId || ''
      }

      // Handle pictureId based on mode
      if (mode === FormModeEnum.CREATE) {
        // In CREATE mode: only add pictureId if a picture is selected
        if (picture?.id) {
          submissionData.pictureId = picture.id
        }
      } else {
        // In UPDATE mode: always send pictureId, empty string to clear
        submissionData.pictureId = picture?.id || ''
      }

      // Only add active if it's defined (to allow setting false)
      if (data.active !== undefined) {
        submissionData.active = data.active
      }

      // Add translations, ensuring all locales are included
      locales.forEach(locale => {
        submissionData.translations[locale.fullCode] = {
          name: data.translations[locale.fullCode]?.name || '',
          description: data.translations[locale.fullCode]?.description || ''
        }
      })

      // Add properties with order
      if (selectedProperties.length > 0) {
        submissionData.properties = selectedProperties.map((property, index) => ({
          id: property.id,
          order: index + 1
        }))
      }

      onSubmitAndNew?.(submissionData)
    }

    const isCreateModeWithSubmitAndNew = mode === FormModeEnum.CREATE && !!onSubmitAndNew

    if (!metaState.response?.locales) {
      return (
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-10 w-full" />
            </div>
            <div className="space-y-4">
              <Skeleton className="h-6 w-32" />
              <div className="flex gap-2">
                <Skeleton className="h-10 w-24" />
                <Skeleton className="h-10 w-24" />
                <Skeleton className="h-10 w-24" />
              </div>
              <div className="space-y-4">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-20 w-full" />
              </div>
            </div>
          </CardContent>
        </Card>
      )
    }

    return (
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(
            isCreateModeWithSubmitAndNew ? handleSubmitAndNew : handleSubmit,
            scrollToFirstError
          )}
          className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FolderTreeIcon className="h-5 w-5" />
                {mode === FormModeEnum.CREATE ? t('create_category') : t('edit_category')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Parent Category Field */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-1 lg:grid-cols-1">
                <FormField
                  control={form.control}
                  name="parentId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">{t('parent_category')}</FormLabel>
                      <FormControl>
                        <CategoryCombobox
                          value={field.value}
                          onValueChange={v => field.onChange(v)}
                          disabled={isLoading}
                          clearable
                          excludeId={mode === FormModeEnum.UPDATE && category ? category.id : undefined}
                          error={!!formErrors.parentId}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Translations */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <LanguagesIcon className="h-5 w-5" />
                  <h3 className="text-lg font-semibold">{t('translations')}</h3>
                </div>

                {locales.length > 0 ? (
                  <Tabs
                    defaultValue={locales[0].fullCode}
                    className="w-full"
                    onValueChange={value => {
                      setTimeout(() => {
                        const input = document.querySelector(`[name="translations.${value}.name"]`) as HTMLInputElement
                        if (input) {
                          input.focus()
                        }
                      }, 100)
                    }}>
                    <TabsList className="min-h-10.5 w-full">
                      {locales.map(locale => {
                        const hasNameError = hasLocaleError(locale.fullCode, 'name')
                        const hasDescError = hasLocaleError(locale.fullCode, 'description')
                        const hasError = hasNameError || hasDescError

                        return (
                          <TabsTrigger
                            key={locale.fullCode}
                            value={locale.fullCode}
                            className={`relative flex items-center gap-2 ${
                              hasError ? 'text-destructive border-destructive' : ''
                            }`}>
                            <Flag country={getCountryCodeFromLocale(locale.fullCode)} size={16} />
                            <span className="hidden sm:inline">{locale.name}</span>
                            {hasError && (
                              <div className="bg-destructive absolute -top-1 -right-1 h-2 w-2 animate-pulse rounded-full" />
                            )}
                          </TabsTrigger>
                        )
                      })}
                    </TabsList>

                    {locales.map(locale => {
                      const hasNameError = hasLocaleError(locale.fullCode, 'name')
                      const hasDescError = hasLocaleError(locale.fullCode, 'description')
                      const hasError = hasNameError || hasDescError

                      return (
                        <TabsContent
                          key={locale.fullCode}
                          value={locale.fullCode}
                          forceMount
                          className="mt-6 space-y-4 data-[state=inactive]:hidden">
                          <div className="flex items-center gap-2">
                            <Flag country={getCountryCodeFromLocale(locale.fullCode)} size={20} />
                            <h4 className="text-lg font-semibold">{locale.name}</h4>
                            <Badge variant="outline" className="text-xs">
                              {locale.fullCode}
                            </Badge>
                            {hasError && (
                              <Badge variant="destructive" className="animate-pulse text-xs">
                                {t('error')}
                              </Badge>
                            )}
                          </div>

                          <FormField
                            control={form.control}
                            name={`translations.${locale.fullCode}.name`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel required>{t('name')}</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder={t('name')}
                                    className={`${hasNameError ? 'border-destructive focus:border-destructive' : ''}`}
                                    {...field}
                                    value={field.value ?? ''}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name={`translations.${locale.fullCode}.description`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{t('description')}</FormLabel>
                                <FormControl>
                                  <Editor
                                    placeholder={t('description')}
                                    value={field.value ?? ''}
                                    autoFocus={false}
                                    onChange={html => field.onChange(html)}
                                    isError={hasDescError}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TabsContent>
                      )
                    })}
                  </Tabs>
                ) : (
                  <div className="py-8 text-center">
                    <p className="text-muted-foreground">{t('no_languages_available')}</p>
                  </div>
                )}
              </div>

              <Separator />

              {/* Properties */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <TagIcon className="h-5 w-5" />
                  <h3 className="text-lg font-semibold">{t('properties')}</h3>
                </div>

                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Combobox
                      options={propertyOptions}
                      value=""
                      onValueChange={handlePropertySelect}
                      placeholder={t('select')}
                      disabled={isLoading || isLoadingProperties || availableProperties.length === 0}
                      icon={<TagIcon className="h-4 w-4" />}
                      className="flex-1"
                    />
                  </div>

                  {selectedProperties.length > 0 ? (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handlePropertyDragEnd}>
                      <SortableContext items={selectedProperties.map(p => p.id)} strategy={verticalListSortingStrategy}>
                        <div className="space-y-2">
                          {selectedProperties.map((property, index) => (
                            <SortablePropertyItem
                              key={property.id}
                              property={property}
                              order={index + 1}
                              onRemove={() => handlePropertyRemove(property.id)}
                              locale={locale}
                              locales={locales}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  ) : (
                    <div className="text-muted-foreground rounded-md border border-dashed p-6 text-center text-sm">
                      {t('no_selected')}
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              {/* Picture */}
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name={'pictureId'}
                  render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                />
                <div className="flex items-center gap-2">
                  <ImageIcon className="h-5 w-5" />
                  <h3 className="font-semibold">{t('picture')}</h3>
                </div>
                <div>
                  <FileSelector
                    multiple={false}
                    onlyImages={true}
                    optimizeImageSelection={true}
                    value={picture}
                    onChange={handlePictureChange}
                    buttonLabel={t('select_picture')}
                    buttonLabelChange={t('change_picture')}
                    noSelectionText={t('no_file_selected')}
                    allowMainSelection={true}
                    initialMainId={category?.picture?.id}
                  />
                  {formErrors.pictureId && (
                    <p className="text-destructive mt-2 text-sm">
                      {String(formErrors.pictureId.message || t('required'))}
                    </p>
                  )}
                </div>

                <Separator />

                <FormField
                  control={form.control}
                  name="active"
                  render={({ field }) => (
                    <FormItem className="flex flex-col items-start justify-start">
                      <div className="space-y-0.5">
                        <FormLabel>{t('active')}</FormLabel>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between gap-3 pt-1">
                <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
                  <XIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('cancel')}</span>
                </Button>
                <div className="flex items-center gap-2">
                  {isCreateModeWithSubmitAndNew && (
                    <Button
                      type="button"
                      onClick={() => form.handleSubmit(handleSubmit, scrollToFirstError)()}
                      disabled={isLoading}>
                      <SaveIcon className="h-4 w-4" />
                      <span className="hidden sm:inline">{isLoading ? t('saving') : t('save')}</span>
                    </Button>
                  )}
                  <Button type="submit" disabled={isLoading}>
                    {isCreateModeWithSubmitAndNew ? (
                      <PlusCircleIcon className="h-4 w-4" />
                    ) : (
                      <SaveIcon className="h-4 w-4" />
                    )}
                    <span className="hidden sm:inline">
                      {isLoading ? t('saving') : isCreateModeWithSubmitAndNew ? t('save_and_add_new') : t(mode)}
                    </span>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          <UnsavedChangesGuardDialog
            open={isNavigationBlocked}
            onConfirm={confirmNavigation}
            onCancel={cancelNavigation}
          />
        </form>
      </Form>
    )
  }
)

CategoryForm.displayName = 'CategoryForm'

export default CategoryForm
